import React, {useState, useRef, useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {ForecastTheme, tokensForThemeProvider} from '@forecast-it/design-system';
import {
	NOVA_PURPLE,
	NOVA_PURPLE_DARK,
	TEXT_MAIN,
	TEXT_AUX,
	TEXT_MED,
	TEXT_BODY,
	TEXT_BANNER,
	TEXT_FIELD_LABEL,
	ERROR_COLOR,
	ERROR_COLOR_3,
	SUCCESS_COLOR,
	BG_WHITE,
	BG_DISABLED,
	BG_PILL_HOVER,
	BG_DELIVERABLE_ROW,
	BORDER_DELIVERABLE_ROW,
	BG_PLACEHOLDER_ROW,
	BORDER_PLACEHOLDER_ROW,
	BG_CLIENT_DROPDOWN_HOVER,
	BORDER_INPUT,
	BORDER_DIVIDER,
	TEXT_DISABLED_GRAY,
} from './colors';
import ForecastTooltip from '../../../forecast-app/shared/components/tooltips/ForecastTooltip';
import Util from '../../../forecast-app/shared/util/util';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import type {ProjectProposal, PlaceholderProgress, ExtractedInvoice} from './types';
import {
	deriveInvoiceTypeFromBudgetType,
	deriveNovaFilledFields,
	INVOICE_TYPE_LABEL_IDS,
	INVOICE_TYPE_OPTIONS,
	SimpleUUID,
} from './constants';
import EditableExtractedList, {ExtractedListItem} from './EditableExtractedList';

// Pre-TNSR-635 review form, restored for the project_create_modal_redesign flag's OFF path.
// Deleted by this same ticket (commit 11686762eb6c: "OverviewSection, DetailsSection and
// FinancialSetupSection replaced every field it rendered") once the new Accordion-based layout
// took over. Every styled-component below carries its original, pre-redesign CSS, defined
// locally rather than reaching into the current styles.ts — whose same-named exports (where
// they still exist at all) now serve the new form's different layout. Per product's explicit
// choice, this is a literal field-for-field revival: Rate Card, Use Baseline, Custom Project ID,
// Labels and the Duplicate Project toggle — all added later in this same ticket — are
// unavailable here, exactly as they were unavailable in the form this replaces.

const FONT = "'neue-haas-grotesk-text', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const {
	sys: {
		color: {id: _idColors},
	},
} = tokensForThemeProvider[ForecastTheme.Sky];
const DELIVERABLE_COLORS = [_idColors['9'], _idColors['7'], _idColors['11'], _idColors['2'], _idColors['5']];

const ReviewFormPanel = Styled.div`
	flex: 1; display: flex; flex-direction: column; overflow: hidden;
`;
const ReviewFormScroll = Styled.div`flex: 1; overflow-y: auto; padding: 0 24px 24px;`;
const SowBanner = Styled.div`
	margin: 16px 0 20px; padding: 10px 14px;
	background: rgba(110,15,234,0.04); border: 1px solid rgba(110,15,234,0.12);
	border-radius: 8px; font-size: 12.5px; color: ${TEXT_BANNER};
	display: flex; align-items: flex-start; gap: 8px; line-height: 1.5;
`;
const SowBannerViewSource = Styled.a`
	font-size: 12px; font-weight: 600; color: ${NOVA_PURPLE}; text-decoration: none;
	white-space: nowrap; margin-left: auto; align-self: center; padding-left: 8px; cursor: pointer;
	&:hover { text-decoration: underline; }
`;
const FormSectionHeader = Styled.div`
	font-size: 10px; font-weight: 700; letter-spacing: 0.8px; color: ${TEXT_AUX};
	text-transform: uppercase; padding: 16px 0 8px;
	border-bottom: 1px solid ${BORDER_DIVIDER}; margin-bottom: 12px;
`;
// SOW review form uses sentence-case labels (Figma design), not the uppercase FieldLabel
const SowFieldLabel = Styled.div`
	font-size: 12px;
	font-weight: 500;
	color: ${TEXT_FIELD_LABEL};
	margin-bottom: 4px;
`;
const NovaFieldWrapper = Styled.div`position: relative;`;
const NovaIndicatorBadge = Styled.div`
	position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
	font-size: 11px; color: ${NOVA_PURPLE}; pointer-events: none; opacity: 0.7;
`;
const ReviewFooter = Styled.div`
	padding: 10px 24px; font-size: 12px; color: ${TEXT_MED};
	border-top: 1px solid ${BORDER_DIVIDER}; display: flex; align-items: center; gap: 4px;
`;
const FieldGroup = Styled.div`
  margin-bottom: 14px;
`;
const FieldInput = Styled.input`
  width: 100%;
  border: 1px solid #e0e0e8;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: ${FONT};
  color: #2e2541;
  box-sizing: border-box;
  outline: none;
  background: #fff;
  &:focus { border-color: #6e0fea; box-shadow: 0 0 0 2px rgba(110,15,234,0.08); }
  &:read-only { background: #fafafa; color: #a1a1a1; cursor: default; }
`;
const DateRow = Styled.div`
  display: flex;
  gap: 12px;
`;
const DateFieldGroup = Styled.div`
  flex: 1;
`;
const BudgetPillRow = Styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;
const BudgetPill = Styled.button<{selected: boolean}>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-family: ${FONT};
  cursor: pointer;
  border: none;
  background: ${({selected}) => (selected ? NOVA_PURPLE : BG_DISABLED)};
  color: ${({selected}) => (selected ? BG_WHITE : TEXT_BODY)};
  transition: background 0.15s, color 0.15s;
  &:hover:not(:disabled) {
    background: ${({selected}) => (selected ? NOVA_PURPLE_DARK : BG_PILL_HOVER)};
  }
`;
const DeliverableRow = Styled.div`
	display: flex; align-items: center; gap: 10px;
	padding: 8px 14px; border-radius: 8px;
	background: ${BG_DELIVERABLE_ROW}; border: 1px solid ${BORDER_DELIVERABLE_ROW}; margin-bottom: 6px;
`;
const DeliverableColorDot = Styled.div<{color?: string}>`
	width: 10px; height: 10px; border-radius: 50%;
	background: ${({color}) => color || TEXT_AUX}; flex-shrink: 0;
`;
const DeliverableName = Styled.div`flex: 1; font-size: 13px; font-weight: 500; color: ${TEXT_MAIN};`;
const DeliverableMeta = Styled.div`font-size: 12px; color: ${TEXT_AUX}; white-space: nowrap;`;

const PlaceholderRow = Styled.div`
	display: flex; align-items: center; gap: 10px;
	padding: 7px 14px; border-radius: 8px;
	background: ${BG_PLACEHOLDER_ROW}; border: 1px solid ${BORDER_PLACEHOLDER_ROW}; margin-bottom: 6px;
`;
const PlaceholderRoleName = Styled.div`flex: 1; font-size: 13px; font-weight: 500; color: ${TEXT_MAIN};`;
const PlaceholderStatusLabel = Styled.div<{status: 'pending' | 'creating' | 'done' | 'failed'}>`
	font-size: 11px; font-weight: 600; white-space: nowrap;
	color: ${({status}) =>
		status === 'done'
			? SUCCESS_COLOR
			: status === 'failed'
			? ERROR_COLOR_3
			: status === 'creating'
			? NOVA_PURPLE
			: TEXT_AUX};
`;

const ClientDropdownChevron = Styled.div`
	position: absolute;
	right: 10px;
	top: 50%;
	transform: translateY(-50%);
	pointer-events: none;
	color: ${TEXT_AUX};
	font-size: 11px;
	line-height: 1;
`;
const ClientDropdownList = Styled.ul`
	position: absolute;
	top: calc(100% + 2px);
	left: 0;
	right: 0;
	z-index: 100;
	background: ${BG_WHITE};
	border: 1px solid ${BORDER_INPUT};
	border-radius: 6px;
	max-height: 180px;
	overflow-y: auto;
	list-style: none;
	margin: 0;
	padding: 4px 0;
	box-shadow: 0 4px 12px rgba(0,0,0,0.08);
`;
const ClientDropdownItem = Styled.li`
	padding: 7px 12px;
	font-size: 13px;
	font-family: ${FONT};
	color: ${TEXT_MAIN};
	cursor: pointer;
	&:hover { background: ${BG_CLIENT_DROPDOWN_HOVER}; }
`;
const WarningsBanner = Styled.div`
	background: #fff8e6;
	border: 1px solid #f3a030;
	border-radius: 4px;
	padding: 8px 12px;
	font-size: 12px;
	color: #535353;
	margin-bottom: 12px;
`;

// Tracks per-row identity for the editable, Nova-extracted invoices list. The domain shape
// (ExtractedInvoice) carries neither a stable id nor a per-row Nova-provenance flag, and every
// edit round-trips the array through `update(...)` — so both would be lost on every render if
// re-derived fresh from `proposal.invoices` each time. This hook keeps them in local state
// instead, refreshing only when the domain array changes for a reason other than this
// component's own last edit (i.e. a genuinely new array arrived from Nova — initial extraction,
// or a reconciliation update before the user has edited this section; see
// AiProjectPlanningModal's mergeProposalEdits for why that stops happening once the user has
// made an edit here).
interface TrackedRow<T> {
	id: string;
	isNova: boolean;
	source: T;
}

// Stable fallback for a missing/undefined domain array — `proposal.invoices ?? []` would
// synthesize a brand-new (and therefore always-"changed") array literal on every render, which
// would make useTrackedRows's effect below fire, call setState, and re-render forever.
const EMPTY_INVOICES: ExtractedInvoice[] = [];

function useTrackedRows<T extends {name: string}>(domainArray: T[], isNovaSection: boolean) {
	const [rows, setRows] = useState<TrackedRow<T>[]>(() =>
		domainArray.map(source => ({id: SimpleUUID(), isNova: isNovaSection, source}))
	);
	const lastAppliedRef = useRef<T[]>(domainArray);

	useEffect(() => {
		if (domainArray === lastAppliedRef.current) return;
		lastAppliedRef.current = domainArray;
		setRows(domainArray.map(source => ({id: SimpleUUID(), isNova: isNovaSection, source})));
		// isNovaSection is intentionally left out of the dependency array below: this effect
		// should re-run only when a new array reference arrives (fresh data), not when the
		// section's Nova-filled flag flips on its own — re-deriving on that too would wipe user
		// edits' row identities for no reason.
	}, [domainArray]);

	// Applies an EditableExtractedList onChange: folds the (possibly edited) name back into
	// each row's `source`, keeping every other field on that row untouched, and returns the
	// resulting raw domain array for the caller to persist via `update(...)`.
	const commit = (newItems: ExtractedListItem<T>[]): T[] => {
		const nextRows: TrackedRow<T>[] = newItems.map(item => ({
			id: item.id,
			isNova: item.isNova,
			source: {...(item.source as T), name: item.name},
		}));
		const nextDomain = nextRows.map(r => r.source);
		lastAppliedRef.current = nextDomain;
		setRows(nextRows);
		return nextDomain;
	};

	return {rows, commit};
}

interface LegacySowReviewFormPanelProps {
	proposal: ProjectProposal;
	novaFilledFields: string[];
	sowFilename: string | null;
	missingRequiredFields?: string[];
	createPlaceholdersEnabled?: boolean;
	onCreatePlaceholdersChange?: (enabled: boolean) => void;
	onProposalChange: (proposal: ProjectProposal) => void;
	onFieldUserEdited?: (field: string) => void;
	onViewSource?: () => void;
	clients?: {id: string; name: string}[];
	persons?: {id: string; backendId: number; fullName: string}[];
	placeholderProgresses?: PlaceholderProgress[];
	isBlocked?: boolean;
}

// Fields the backend can Nova-fill that correspond to visible form fields.
// rate_card_id is backend-only and excluded — it would inflate novaCount above totalFields.
// budget is excluded for NON_BILLABLE projects (field is hidden), handled via totalFields.
const NOVA_FORM_FIELDS = new Set([
	'name',
	'client_id',
	'start_date',
	'end_date',
	'budget_type',
	'budget',
	'deliverables',
	'invoices',
]);

const LegacySowReviewFormPanel: React.FC<LegacySowReviewFormPanelProps> = ({
	proposal,
	novaFilledFields,
	sowFilename,
	missingRequiredFields = [],
	createPlaceholdersEnabled = false,
	onCreatePlaceholdersChange,
	onProposalChange,
	onFieldUserEdited,
	onViewSource,
	clients = [],
	persons = [],
	placeholderProgresses,
	isBlocked = false,
}) => {
	const {formatMessage, formatNumber} = useIntl();
	const update = <K extends keyof ProjectProposal>(field: K, value: ProjectProposal[K]) =>
		onProposalChange({...proposal, [field]: value});
	// When backend omits nova_filled_fields, derive from proposal's non-null values so
	// the count is always accurate (covers reconciliation phase and session-restore paths).
	// When the SOW is blocked (retainer/MSA), Nova never ran extraction so nothing counts
	// as Nova-filled — the proposal holds only defaults set before the block fired.
	const effectiveNovaFields = React.useMemo(() => {
		if (isBlocked) return [];
		if (novaFilledFields.length > 0) return novaFilledFields;
		return deriveNovaFilledFields(proposal);
	}, [isBlocked, novaFilledFields, proposal]);
	const isNova = (field: string) => effectiveNovaFields.includes(field);

	// client_name may hold a raw name Nova extracted from the SoW that doesn't match any
	// real Forecast client — client_id stays null until the user (or reconciliation) picks
	// one. Only ever show client_name once client_id is actually resolved, so the search
	// box never displays an unresolved guess as if it were a confirmed selection.
	const resolvedClientName = proposal.client_id != null ? proposal.client_name ?? '' : '';

	const [clientSearch, setClientSearch] = useState(resolvedClientName);
	const [showClientDropdown, setShowClientDropdown] = useState(false);
	const [clientUserIsSearching, setClientUserIsSearching] = useState(false);
	const filteredClients = useMemo(
		() =>
			clientUserIsSearching ? clients.filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase())) : clients,
		[clientUserIsSearching, clients, clientSearch]
	);

	useEffect(() => {
		setClientSearch(resolvedClientName);
	}, [resolvedClientName]);

	const [ownerSearch, setOwnerSearch] = useState(proposal.owner_name ?? '');
	const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
	const [ownerUserIsSearching, setOwnerUserIsSearching] = useState(false);
	const filteredPersons = useMemo(
		() =>
			ownerUserIsSearching ? persons.filter(p => p.fullName.toLowerCase().includes(ownerSearch.toLowerCase())) : persons,
		[ownerUserIsSearching, persons, ownerSearch]
	);

	useEffect(() => {
		setOwnerSearch(proposal.owner_name ?? '');
	}, [proposal.owner_name]);

	// Sticky for the same reason a plain-length check would flicker: gating on the *current*
	// invoices length would hide the whole section — including "Add invoice manually" — the
	// moment the user removes every row. Once Nova (or the user) has put at least one invoice
	// in the proposal, keep the section mounted even if it's later emptied out. Deliverables has
	// no such state — it's read-only, so its visibility is a plain "does the array have entries"
	// check with nothing that can empty it back out from under the user.
	const [showInvoicesSection, setShowInvoicesSection] = useState(() => (proposal.invoices?.length ?? 0) > 0);
	useEffect(() => {
		if ((proposal.invoices?.length ?? 0) > 0) setShowInvoicesSection(true);
	}, [proposal.invoices]);

	// Per-row stable id + Nova-provenance for the editable invoices list — see useTrackedRows above.
	const invoicesTracked = useTrackedRows<ExtractedInvoice>(proposal.invoices ?? EMPTY_INVOICES, isNova('invoices'));

	const novaCount = effectiveNovaFields.filter(f => NOVA_FORM_FIELDS.has(f)).length;
	// Gated on the STICKY showInvoicesSection flag, not the live array length, for the same
	// reason as its own comment above: novaCount comes from effectiveNovaFields, which is locked
	// at first extraction and never decreases, so a user emptying a Nova-filled invoices list
	// must not decrease totalFields either, or the footer can read "filled 8 of 7". Deliverables
	// has no such flag to gate on — it's read-only, so the live length is exactly its own state.
	const totalFields =
		5 + // name, client_id, start_date, end_date, budget_type
		(proposal.budget_type === 'FIXED_PRICE' ? 1 : 0) + // budget amount (FIXED_PRICE only)
		(proposal.deliverables?.length ? 1 : 0) + // deliverables section
		(showInvoicesSection ? 1 : 0); // invoices section

	// Row meta line for an invoice: "<due date> · <amount> · <type>", built the same
	// conditionally-joined way as the deliverables meta above so a missing due date doesn't
	// leave a stray leading separator. `unit_price` has no currency code attached anywhere in
	// this proposal — the project's currency isn't resolved until after creation — so this
	// formats it as a locale-aware number (thousands separators, 2 decimals) via react-intl's
	// formatNumber rather than guessing at a currency symbol that could be wrong for the
	// client. `invoice_type` is a backend enum and must go through INVOICE_TYPE_LABEL_IDS
	// rather than render raw ("DEPOSIT", "FIXED_PRICE", "TIME_AND_MATERIAL").
	const formatInvoiceMeta = (inv: ExtractedInvoice): string => {
		const amount = formatNumber(inv.unit_price, {minimumFractionDigits: 2, maximumFractionDigits: 2});
		// An invoice_type this map doesn't recognise is omitted, the same way a missing
		// due_date is — never mislabelled as Fixed price, which is a claim about the data
		// this code cannot back up.
		const typeLabelId = INVOICE_TYPE_LABEL_IDS[inv.invoice_type];
		const typeLabel = typeLabelId ? formatMessage({id: typeLabelId}) : null;
		return [inv.due_date, amount, typeLabel].filter((part): part is string => Boolean(part)).join(' · ');
	};

	// A manually-added invoice row (makeBlank's {unit_price: 0}) has no Nova-supplied amount and
	// no other way to set one — EditableExtractedList's own row editing only covers `name` — so
	// without this, "Add invoice manually" could only ever produce a $0.00 invoice. Wired only
	// for non-Nova rows; a Nova-extracted amount stays read-only display text via formatInvoiceMeta.
	const updateInvoiceUnitPrice = (rowId: string, raw: string) => {
		if (raw !== '' && isNaN(Number(raw.replace(/,/g, '')))) return;
		const amount = raw === '' ? 0 : Number(raw.replace(/,/g, ''));
		if (amount < 0) return;
		const newItems = invoicesTracked.rows.map(row => ({
			id: row.id,
			name: row.source.name,
			meta: formatInvoiceMeta(row.source),
			isNova: row.isNova,
			source: row.id === rowId ? {...row.source, unit_price: amount} : row.source,
		}));
		onFieldUserEdited?.('invoices');
		update('invoices', invoicesTracked.commit(newItems));
	};

	// A manually-added invoice row's due date starts blank (per product decision — no silent
	// "today" default shown as if it were a real chosen value) and is editable the same way
	// start_date/end_date are in the Basic section above. An empty string means "not set",
	// stored as null — create_draft_invoice's own fallback-to-today already handles a null due
	// date at commit time, so leaving it blank here doesn't skip that safety net.
	const updateInvoiceDueDate = (rowId: string, value: string) => {
		const newItems = invoicesTracked.rows.map(row => ({
			id: row.id,
			name: row.source.name,
			meta: formatInvoiceMeta(row.source),
			isNova: row.isNova,
			source: row.id === rowId ? {...row.source, due_date: value === '' ? null : value} : row.source,
		}));
		onFieldUserEdited?.('invoices');
		update('invoices', invoicesTracked.commit(newItems));
	};

	// A manually-added invoice row's type defaults from the project's budget_type (see
	// deriveInvoiceTypeFromBudgetType) rather than a hardcoded Fixed Price, but stays editable —
	// this only changes the row's current value when the user picks a different one.
	const updateInvoiceType = (rowId: string, value: string) => {
		const newItems = invoicesTracked.rows.map(row => ({
			id: row.id,
			name: row.source.name,
			meta: formatInvoiceMeta(row.source),
			isNova: row.isNova,
			source: row.id === rowId ? {...row.source, invoice_type: value} : row.source,
		}));
		onFieldUserEdited?.('invoices');
		update('invoices', invoicesTracked.commit(newItems));
	};

	return (
		<ReviewFormPanel data-cy="legacy-sow-review-panel">
			<ReviewFormScroll>
				<SowBanner>
					<span aria-hidden="true" style={{color: NOVA_PURPLE, flexShrink: 0}}>
						✦
					</span>
					<span>
						{formatMessage(
							{id: 'sow_review.nova_prefilled_banner'},
							{filename: <em key="fn">{sowFilename ?? 'your SoW'}</em>}
						)}
					</span>
					{onViewSource && (
						<SowBannerViewSource onClick={onViewSource}>
							{formatMessage({id: 'sow_review.view_source'})}
						</SowBannerViewSource>
					)}
				</SowBanner>

				<FormSectionHeader>{formatMessage({id: 'sow_review.section_basics'})}</FormSectionHeader>

				<FieldGroup>
					<SowFieldLabel>{formatMessage({id: 'ai_project_planning.field_project_name'})}</SowFieldLabel>
					<NovaFieldWrapper>
						<FieldInput
							data-cy="legacy-sow-name"
							type="text"
							value={proposal.name}
							onChange={e => {
								onFieldUserEdited?.('name');
								update('name', e.target.value);
							}}
						/>
						{isNova('name') && <NovaIndicatorBadge>✦</NovaIndicatorBadge>}
					</NovaFieldWrapper>
				</FieldGroup>

				<DateRow>
					<DateFieldGroup>
						<SowFieldLabel>{formatMessage({id: 'sow_review.field_client'})}</SowFieldLabel>
						<NovaFieldWrapper>
							<FieldInput
								data-cy="legacy-sow-client"
								type="text"
								value={clientSearch}
								placeholder="Search clients…"
								style={{paddingRight: 28, cursor: 'pointer'}}
								onChange={e => {
									setClientSearch(e.target.value);
									setClientUserIsSearching(true);
									setShowClientDropdown(true);
								}}
								onFocus={() => {
									setClientUserIsSearching(false);
									setShowClientDropdown(true);
								}}
								onBlur={() =>
									setTimeout(() => {
										setShowClientDropdown(false);
										// Snap back to last valid selection — prevents a stale client_id
										// remaining in the proposal when the user types then blurs without picking.
										setClientSearch(resolvedClientName);
									}, 150)
								}
							/>
							{/* Nova badge and chevron share the same right:10px slot — only one renders at a time */}
							{isNova('client_id') ? (
								<NovaIndicatorBadge>✦</NovaIndicatorBadge>
							) : (
								<ClientDropdownChevron>▾</ClientDropdownChevron>
							)}
							{showClientDropdown && filteredClients.length > 0 && (
								<ClientDropdownList>
									{filteredClients.map(c => (
										<ClientDropdownItem
											key={c.id}
											data-cy="legacy-sow-client-option"
											onMouseDown={() => {
												const clientId = Util.getIdFromBase64String(c.id);
												if (!isNaN(clientId)) {
													onFieldUserEdited?.('client_id');
													onFieldUserEdited?.('client_name');
													onProposalChange({
														...proposal,
														client_id: clientId,
														client_name: c.name,
													});
												}
												setClientSearch(c.name);
												setClientUserIsSearching(false);
												setShowClientDropdown(false);
											}}
										>
											{c.name}
										</ClientDropdownItem>
									))}
								</ClientDropdownList>
							)}
						</NovaFieldWrapper>
					</DateFieldGroup>
					<DateFieldGroup>
						<SowFieldLabel>{formatMessage({id: 'sow_review.field_project_owner'})}</SowFieldLabel>
						<NovaFieldWrapper>
							<FieldInput
								type="text"
								value={ownerSearch}
								placeholder={formatMessage({id: 'sow_review.field_project_owner_placeholder'})}
								style={{paddingRight: 28, cursor: 'pointer'}}
								onChange={e => {
									setOwnerSearch(e.target.value);
									setOwnerUserIsSearching(true);
									setShowOwnerDropdown(true);
									// Mark as user-edited when they clear the field — prevents
									// bot proposals from silently re-inserting the viewer identity.
									if (!e.target.value) onFieldUserEdited?.('owner_person_id');
								}}
								onFocus={() => {
									setOwnerUserIsSearching(false);
									setShowOwnerDropdown(true);
								}}
								onBlur={() =>
									setTimeout(() => {
										setShowOwnerDropdown(false);
										setOwnerSearch(proposal.owner_name ?? '');
									}, 150)
								}
							/>
							<ClientDropdownChevron>▾</ClientDropdownChevron>
							{showOwnerDropdown && filteredPersons.length > 0 && (
								<ClientDropdownList>
									{filteredPersons.map(p => (
										<ClientDropdownItem
											key={p.id}
											onMouseDown={() => {
												onFieldUserEdited?.('owner_person_id');
												onProposalChange({
													...proposal,
													owner_person_id: p.backendId,
													owner_name: p.fullName,
												});
												setOwnerSearch(p.fullName);
												setOwnerUserIsSearching(false);
												setShowOwnerDropdown(false);
											}}
										>
											{p.fullName}
										</ClientDropdownItem>
									))}
								</ClientDropdownList>
							)}
						</NovaFieldWrapper>
					</DateFieldGroup>
				</DateRow>

				<DateRow>
					<DateFieldGroup>
						<SowFieldLabel>{formatMessage({id: 'ai_project_planning.field_start_date'})}</SowFieldLabel>
						<NovaFieldWrapper>
							<FieldInput
								data-cy="legacy-sow-start-date"
								type="date"
								value={proposal.start_date ?? ''}
								onChange={e => {
									onFieldUserEdited?.('start_date');
									update('start_date', e.target.value);
								}}
							/>
							{isNova('start_date') && <NovaIndicatorBadge>✦</NovaIndicatorBadge>}
						</NovaFieldWrapper>
					</DateFieldGroup>
					<DateFieldGroup>
						<SowFieldLabel>{formatMessage({id: 'ai_project_planning.field_end_date'})}</SowFieldLabel>
						<NovaFieldWrapper>
							<FieldInput
								data-cy="legacy-sow-end-date"
								type="date"
								value={proposal.end_date ?? ''}
								onChange={e => {
									onFieldUserEdited?.('end_date');
									update('end_date', e.target.value);
								}}
							/>
							{isNova('end_date') && <NovaIndicatorBadge>✦</NovaIndicatorBadge>}
						</NovaFieldWrapper>
					</DateFieldGroup>
				</DateRow>

				{(proposal.deliverables?.length ?? 0) > 0 && (
					<>
						<FormSectionHeader>
							{formatMessage({id: 'sow_review.section_deliverables'}, {count: proposal.deliverables.length})}
						</FormSectionHeader>
						{proposal.deliverables.map((d, i) => (
							<DeliverableRow key={d.name + i}>
								<DeliverableColorDot color={DELIVERABLE_COLORS[i % DELIVERABLE_COLORS.length]} />
								<DeliverableName>{d.name}</DeliverableName>
								<DeliverableMeta>
									{d.start_date && d.end_date ? `${d.start_date} – ${d.end_date}` : ''}
									{d.total_hours ? ` · ${d.total_hours} hrs` : ''}
								</DeliverableMeta>
							</DeliverableRow>
						))}
					</>
				)}

				{(showInvoicesSection || (proposal.unsupported_billing_terms?.length ?? 0) > 0) && (
					<>
						{/* Gated on the same sticky showInvoicesSection flag as the section below, not
						 * the live invoices length — a coordinator who deletes every extracted row
						 * still has "Add invoice manually" reachable (the section stays mounted), and
						 * whatever they add there is still silently dropped at commit. The banner must
						 * stay visible for exactly as long as that risk does, not just while the array
						 * happens to be non-empty. */}
						{!hasPermission(PERMISSION_TYPE.INVOICE_CREATE) && showInvoicesSection && (
							<WarningsBanner>{formatMessage({id: 'sow_review.invoices_permission_denied'})}</WarningsBanner>
						)}
						{/* Rendered above the Invoices section, and shows the actual deferred clause
						 * text Nova extracted rather than a generic message — the intro copy is
						 * deliberately generic across all three deferred categories (milestone-based,
						 * conditional/acceptance-gated, retainer draw-down), since any of them can
						 * land in this list. */}
						{(proposal.unsupported_billing_terms?.length ?? 0) > 0 && (
							<WarningsBanner>
								<div>{formatMessage({id: 'sow_review.unsupported_billing_terms_note'})}</div>
								<ul style={{margin: '6px 0 0', paddingLeft: 18}}>
									{(proposal.unsupported_billing_terms ?? []).map((term, i) => (
										<li key={i}>{term}</li>
									))}
								</ul>
							</WarningsBanner>
						)}
						{showInvoicesSection && (
							<>
								<FormSectionHeader>
									{formatMessage(
										{id: 'sow_review.section_invoices'},
										{count: invoicesTracked.rows.filter(row => row.isNova).length}
									)}
								</FormSectionHeader>
								<EditableExtractedList
									items={invoicesTracked.rows.map(row => ({
										id: row.id,
										name: row.source.name,
										meta: formatInvoiceMeta(row.source),
										isNova: row.isNova,
										source: row.source,
										// A Nova-extracted row stays read-only display text (via `meta` above, in
										// "<due date> · <amount> · <type>" order) — only a manually-added row,
										// which has no Nova-supplied values and no other way to set them, gets
										// editable inputs. Same field order as that read-only text, so a row
										// doesn't visually reorder depending on whether it's Nova-extracted or
										// manually added.
										metaFields: row.isNova
											? undefined
											: [
													{
														key: 'due_date',
														label: formatMessage({id: 'sow_review.invoice_due_date_input_label'}),
														type: 'date',
														value: row.source.due_date ?? '',
														onChange: (value: string) => updateInvoiceDueDate(row.id, value),
													},
													{
														key: 'unit_price',
														label: formatMessage({id: 'sow_review.invoice_amount_input_label'}),
														type: 'number',
														value: String(row.source.unit_price),
														onChange: (value: string) => updateInvoiceUnitPrice(row.id, value),
													},
													{
														key: 'invoice_type',
														label: formatMessage({id: 'sow_review.invoice_type_input_label'}),
														type: 'select',
														value: row.source.invoice_type,
														options: INVOICE_TYPE_OPTIONS.map(value => ({
															value,
															label: formatMessage({id: INVOICE_TYPE_LABEL_IDS[value]}),
														})),
														onChange: (value: string) => updateInvoiceType(row.id, value),
													},
											  ],
									}))}
									onChange={newItems => {
										onFieldUserEdited?.('invoices');
										update('invoices', invoicesTracked.commit(newItems));
									}}
									makeBlank={() => ({
										name: '',
										unit_price: 0,
										due_date: null,
										invoice_type: deriveInvoiceTypeFromBudgetType(proposal.budget_type),
									})}
									renderAddLabel={formatMessage({id: 'sow_review.add_invoice_manually'})}
								/>
							</>
						)}
					</>
				)}

				{(placeholderProgresses?.length ?? 0) > 0 && (
					<>
						<FormSectionHeader>
							{formatMessage(
								{id: 'sow_review.section_placeholders'},
								{count: (placeholderProgresses ?? []).length}
							)}
						</FormSectionHeader>
						{(placeholderProgresses ?? []).map((p, i) => (
							<PlaceholderRow key={p.name + i}>
								<PlaceholderRoleName>{p.name}</PlaceholderRoleName>
								<PlaceholderStatusLabel status={p.status}>
									{p.status === 'done'
										? formatMessage({id: 'sow_review.placeholder_status_done'})
										: p.status === 'failed'
										? formatMessage({id: 'sow_review.placeholder_status_failed'})
										: p.status === 'creating'
										? formatMessage({id: 'sow_review.placeholder_status_creating'})
										: formatMessage({id: 'sow_review.placeholder_status_pending'})}
								</PlaceholderStatusLabel>
							</PlaceholderRow>
						))}
					</>
				)}

				<FormSectionHeader>{formatMessage({id: 'sow_review.section_budget'})}</FormSectionHeader>

				<FieldGroup>
					<SowFieldLabel>{formatMessage({id: 'ai_project_planning.field_budget_type'})}</SowFieldLabel>
					<BudgetPillRow>
						{[
							{key: 'FIXED_PRICE', labelId: 'ai_project_planning.budget_fixed_price', isDisabled: false},
							{
								key: 'TIME_AND_MATERIALS',
								labelId: 'ai_project_planning.budget_time_materials',
								isDisabled: false,
							},
							{key: 'NON_BILLABLE', labelId: 'ai_project_planning.budget_non_billable', isDisabled: true},
							{key: 'RETAINER', labelId: 'ai_project_planning.budget_retainer', isDisabled: true},
						].map(({key, labelId, isDisabled}) => (
							<ForecastTooltip
								key={key}
								content={
									isDisabled
										? formatMessage({
												id:
													key === 'NON_BILLABLE'
														? 'ai_project_planning.budget_non_billable_disabled_tooltip'
														: 'ai_project_planning.budget_retainer_disabled_tooltip',
										  })
										: undefined
								}
							>
								<BudgetPill
									data-cy={`legacy-sow-budget-${key}`}
									selected={!isDisabled && proposal.budget_type === key}
									disabled={isDisabled}
									style={
										isDisabled
											? {
													cursor: 'not-allowed',
													opacity: 0.5,
													background: BG_DISABLED,
													color: TEXT_DISABLED_GRAY,
													pointerEvents: 'none',
											  }
											: undefined
									}
									onClick={
										isDisabled
											? undefined
											: () => {
													onFieldUserEdited?.('budget_type');
													onProposalChange({
														...proposal,
														budget_type: key,
													});
											  }
									}
								>
									{formatMessage({id: labelId})}
								</BudgetPill>
							</ForecastTooltip>
						))}
					</BudgetPillRow>
				</FieldGroup>

				{proposal.budget_type === 'FIXED_PRICE' && (
					<FieldGroup>
						<SowFieldLabel>{formatMessage({id: 'sow_review.field_budget_amount'})}</SowFieldLabel>
						<NovaFieldWrapper>
							<FieldInput
								data-cy="legacy-sow-budget-amount"
								type="text"
								inputMode="decimal"
								value={proposal.budget ?? ''}
								placeholder="0"
								onChange={e => {
									onFieldUserEdited?.('budget');
									const raw = e.target.value;
									if (raw === '' || raw === '-') {
										update('budget', null);
									} else {
										const n = Number(raw.replace(/,/g, ''));
										if (!isNaN(n) && n >= 0) update('budget', n);
									}
								}}
							/>
							{isNova('budget') && <NovaIndicatorBadge>✦</NovaIndicatorBadge>}
						</NovaFieldWrapper>
					</FieldGroup>
				)}

				<FieldGroup>
					<label
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 8,
							cursor: 'pointer',
							fontSize: 13,
							color: TEXT_MAIN,
							userSelect: 'none',
						}}
					>
						<input
							data-cy="legacy-sow-create-placeholders"
							type="checkbox"
							checked={createPlaceholdersEnabled}
							onChange={e => onCreatePlaceholdersChange?.(e.target.checked)}
							style={{accentColor: NOVA_PURPLE, width: 14, height: 14, flexShrink: 0}}
						/>
						{formatMessage({id: 'sow_review.create_placeholders_toggle'})}
					</label>
				</FieldGroup>
			</ReviewFormScroll>

			<ReviewFooter>
				<span style={{color: NOVA_PURPLE, fontWeight: 600}}>✦ Nova</span>{' '}
				{formatMessage({id: 'sow_review.footer_filled_rest'}, {filled: novaCount, total: totalFields})}
				{missingRequiredFields.length > 0 && (
					<span style={{color: ERROR_COLOR, marginLeft: 8}}>
						{'· '}
						{missingRequiredFields.join(', ')} still required
					</span>
				)}
			</ReviewFooter>
		</ReviewFormPanel>
	);
};

export default LegacySowReviewFormPanel;
